'use client';

import React, { useEffect, useState } from 'react';
import { Typography, Button, Box } from '@mui/material';
import { Venue, VenueBookedTimeSlot } from '../../../shared/models';
import { TimeRange } from '../../../shared/types';
import AvailabilitiesGridTimeSelector from './availabilities_grid_time_selector';
import { getVenueBookedTimeSlotsByDay } from '../lib/firebase/bookings';
import { useAvailabilityStore } from '../stores/availability_store';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { trackEvent } from '../lib/analytics';
import { useRouter } from 'next/navigation';

interface AvailabilitiesBookingDetailsProps {
    possibleTimeRangeForDay: TimeRange[] | null;
    isNoMinimumSpendRequired: boolean;
    handleOkClick: () => void;
    errorMessage: string | null;
    bookedTimeSlotsByDay: Record<string, VenueBookedTimeSlot[]>;
    handleSelectedTimeChange: (time: TimeRange, section: string, price: string) => void;
    selectedSlot: { StartTime: Date; EndTime: Date } | null;
    selectedStartTime: Date | null;
}

const AvailabilitiesBookingDetails: React.FC<AvailabilitiesBookingDetailsProps> = ({
    possibleTimeRangeForDay,
    isNoMinimumSpendRequired,
    handleOkClick,
    errorMessage,
    bookedTimeSlotsByDay,
    handleSelectedTimeChange,
    selectedSlot,
    selectedStartTime,
}) => {
    const {
        venue,
        selectedSection,
        numberOfPeople,
        openBarSelected,
        totalPrice,
        setSelectedSection,
        setNumberOfPeople,
        setOpenBarSelected,
    } = useAvailabilityStore();
    const [loadedBookedTimeSlots, setLoadedBookedTimeSlots] = useState(bookedTimeSlotsByDay);
    const [hasOverlapError, setHasOverlapError] = useState(false);
    const router = useRouter();
    const [hasPriceBeenViewed, setHasPriceBeenViewed] = useState(false);

    // Track when price is viewed
    useEffect(() => {
        if (totalPrice && totalPrice !== '-' && selectedSection && selectedSlot && !hasPriceBeenViewed) {
            setHasPriceBeenViewed(true);
            trackEvent('Venue Price Viewed', {
                venue_id: venue?.ID,
                section: selectedSection,
                price: Number(totalPrice),
                per_person_price: numberOfPeople ? Number(totalPrice) / numberOfPeople : Number(totalPrice),
                people_count: numberOfPeople,
                open_bar_selected: openBarSelected,
                start_time: selectedSlot.StartTime.toISOString(),
                end_time: selectedSlot.EndTime.toISOString(),
                platform: window.innerWidth <= 768 ? 'mobile' : 'desktop'
            });
        }
    }, [totalPrice, selectedSection, selectedSlot, hasPriceBeenViewed, venue?.ID, numberOfPeople, openBarSelected]);

    // Track user exit after viewing price
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (hasPriceBeenViewed) {
                trackEvent('Exit After Viewing Price', {
                    venue_id: venue?.ID,
                    section: selectedSection,
                    price: totalPrice ? Number(totalPrice) : null,
                    per_person_price: totalPrice && numberOfPeople ? Number(totalPrice) / numberOfPeople : null,
                    people_count: numberOfPeople,
                    open_bar_selected: openBarSelected,
                    time_selected: selectedSlot ? true : false,
                    platform: window.innerWidth <= 768 ? 'mobile' : 'desktop'
                });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasPriceBeenViewed, venue?.ID, selectedSection, totalPrice, numberOfPeople, openBarSelected, selectedSlot]);

    useEffect(() => {
        const loadBookedTimeSlots = async () => {
            if (!Object.keys(bookedTimeSlotsByDay).length && venue?.ID && possibleTimeRangeForDay?.length) {
                const startTime = possibleTimeRangeForDay[0].startTime;
                const endTime = possibleTimeRangeForDay[possibleTimeRangeForDay.length - 1].endTime;
                const slots = await getVenueBookedTimeSlotsByDay(venue.ID, startTime,
                    startTime ? new Date(startTime.getTime() + (2 * 24 * 60 * 60 * 1000)) : undefined);
                setLoadedBookedTimeSlots(slots);
            }
        };
        loadBookedTimeSlots();
    }, [venue?.ID, bookedTimeSlotsByDay, possibleTimeRangeForDay]);

    const handleTimeRangeChange = (startTime: Date, endTime: Date) => {
        handleSelectedTimeChange(
            { startTime, endTime, rangeId: startTime.getTime() },
            selectedSection,
            hasOverlapError ? '0' : (totalPrice || '0')
        );
    };

    const onOverlapErrorChange = (hasError: boolean) => {
        setHasOverlapError(hasError);
    };

    return (
        <>
            <AvailabilitiesGridTimeSelector
                possibleTimeRangeForDay={possibleTimeRangeForDay}
                errorMessage={errorMessage}
                bookedTimeSlotsByDay={loadedBookedTimeSlots}
                selectedSlot={selectedSlot}
                selectedStartTime={selectedStartTime}
                onTimeRangeChange={handleTimeRangeChange}
                onOverlapErrorChange={onOverlapErrorChange}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    mt: 1,
                }}
            >
                {/* {possibleTimeRangeForDay &&
                    venue?.PricingRatesPerSection[selectedSection]?.DepositPercentage === 0 &&
                    (new Date(possibleTimeRangeForDay.startTime).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) > 3 && (
                        <Box
                            sx={{
                                backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                borderRadius: '8px',
                                padding: '4px 8px',
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#C0C0DD', fontWeight: 'bold' }}>
                                🎉 Split payments with friends!
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setIsSplitPaymentInfoModalOpen(true)}
                                sx={{
                                    color: '#C0C0DD',
                                    borderColor: '#C0C0DD',
                                    '&:hover': {
                                        backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                        borderColor: '#C0C0DD',
                                    },
                                }}
                            >
                                Learn More
                            </Button>
                        </Box>
                    )}
                <SplitPaymentInfoModal
                    isOpen={isSplitPaymentInfoModalOpen}
                    onClose={() => setIsSplitPaymentInfoModalOpen(false)}
                /> */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {venue?.PricingRatesPerSection[selectedSection]?.DepositPercentage > 0 ? (
                        <>
                            <Typography variant="caption" sx={{ color: '#C0C0DD', mb: 0.5 }}>
                                Total: ${totalPrice !== null && totalPrice !== '-' ?
                                    Number(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    : '0.00'}
                            </Typography>
                            {isNoMinimumSpendRequired ? (
                                <Typography variant="body2" sx={{ color: '#C0C0DD', mb: 0.5 }}>
                                    No minimum spend required
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#C0C0DD', mb: 0.5 }}>
                                    {numberOfPeople ?
                                        `$${totalPrice !== null && totalPrice !== '-' ?
                                            (Number(totalPrice) / numberOfPeople).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            : '0.00'} each for ${numberOfPeople} people`
                                        : `Total: $${totalPrice !== null && totalPrice !== '-' ?
                                            Number(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            : '0.00'}`
                                    }
                                </Typography>
                            )}
                            <Button
                                variant='contained'
                                onClick={handleOkClick}
                                sx={{
                                    backgroundColor: '#C0C0DD',
                                    color: '#1A1A1A',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    padding: '14px 24px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                    '&:disabled': {
                                        backgroundColor: '#F5E6FA',
                                        color: '#8F8F8F'
                                    },
                                    '&:hover': {
                                        backgroundColor: '#d3a4e3',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)'
                                    }
                                }}
                                disabled={totalPrice === null || totalPrice === '-' || !selectedSection || !!errorMessage || hasOverlapError}
                            >
                                {isNoMinimumSpendRequired ? (
                                    <>
                                        <CalendarTodayOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                        <span>Reserve with refundable deposit</span>
                                    </>
                                ) : (
                                    <>
                                        <ShoppingCartOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                        <span>
                                            Checkout - {totalPrice !== null && totalPrice !== '-' && numberOfPeople ?
                                                `${Number(Number(totalPrice) / numberOfPeople).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per person` :
                                                '0.00'}
                                        </span>
                                    </>
                                )}
                            </Button>
                            {isNoMinimumSpendRequired ? (
                                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Typography variant="caption" sx={{ color: '#C0C0DD', fontWeight: 'medium' }}>
                                        No payment due today
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#C0C0DD', mt: 0.5 }}>
                                        Your deposit is 100% refundable
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#C0C0DD', mt: 0.5 }}>
                                        Enjoy your reservation with no minimum spend
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="caption" sx={{ color: '#C0C0DD', mt: 0.5 }}>
                                    Deposit will be held until event date.
                                    {!!venue.PricingRatesPerSection[selectedSection]?.TowardsMinimumSpend ?
                                        " This is towards a minimum spend. Full amount due at event." : ""}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography variant="caption" sx={{ color: '#C0C0DD', mb: 0.5 }}>
                                Total: ${totalPrice !== null && totalPrice !== '-' ?
                                    Number(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    : '0.00'}
                            </Typography>
                            <Button
                                variant='contained'
                                onClick={handleOkClick}
                                sx={{
                                    backgroundColor: '#C0C0DD',
                                    color: '#1A1A1A',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    padding: '14px 24px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                    '&:disabled': {
                                        backgroundColor: '#F5E6FA',
                                        color: '#8F8F8F'
                                    },
                                    '&:hover': {
                                        backgroundColor: '#d3a4e3',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)'
                                    }
                                }}
                                disabled={totalPrice === null || totalPrice === '-' || !selectedSection || !!errorMessage || hasOverlapError}
                            >
                                {isNoMinimumSpendRequired ? (
                                    <>
                                        <CalendarTodayOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                        <span>Reserve with {totalPrice !== null && totalPrice !== '-' && selectedSection
                                            ? totalPrice
                                            : '0.00'} refundable deposit</span>
                                    </>
                                ) : (
                                    <>
                                        <ShoppingCartOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                        <span>
                                            Checkout - {totalPrice !== null && totalPrice !== '-' && numberOfPeople ?
                                                `${Number(Number(totalPrice) / numberOfPeople).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per person` :
                                                '0.00'}
                                        </span>
                                    </>
                                )}
                            </Button>
                            {!openBarSelected && (
                                isNoMinimumSpendRequired ? (
                                    <Typography variant="caption" sx={{ color: '#C0C0DD', mt: 0.5 }}>
                                        No minimum spend required.
                                    </Typography>
                                ) : (
                                    !!venue.PricingRatesPerSection[selectedSection]?.TowardsMinimumSpend && (
                                        <Typography variant="caption" sx={{ color: '#C0C0DD', mt: 0.5 }}>
                                            This is towards a minimum spend.
                                        </Typography>
                                    )
                                )
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default AvailabilitiesBookingDetails;
